/**
 * Embed Consent Handler
 *
 * Client-side script that handles GDPR consent interactions for social embeds.
 * - Initializes consent overlays on page load
 * - Handles button clicks for accepting consent (persisted via Usercentrics)
 * - Loads embed content after consent is given
 * - Loads platform SDK scripts (Instagram, Twitter, TikTok) when needed
 * - Syncs DOM state when Usercentrics consent changes
 */

import { isServer } from "solid-js/web";
import type { SocialPlatform } from "~/components/renderers/RichTextRenderer";
import { consentActions } from "~/stores/consent";
import { hasEmbedConsent, giveEmbedConsent } from "./embedConsentSync";
import { sanitizeHtml } from "~/utils/htmlSecurity";
import { logger } from "~/lib/logger/logger";

/** Delay before reprocessing embeds so third-party SDKs can pick up new DOM nodes */
const EMBED_REPROCESS_DELAY_MS = 100;

/** Small delay to ensure DOM is ready after SolidStart hydration */
const DOM_READY_DELAY_MS = 50;

const SELECTORS = {
  wrapper: ".embed-consent-wrapper",
  content: ".embed-consent-content",
  consentAction: "[data-consent-action]",
  consentGiven: "consent-given",
} as const;

// --- Platform SDK Script Loading ---

interface PlatformScriptConfig {
  id: string;
  src: string;
  processEmbeds?: () => void;
}

/** Platforms that have a third-party embed SDK script to load */
const PLATFORM_SCRIPTS: Partial<Record<SocialPlatform, PlatformScriptConfig>> = {
  instagram: {
    id: "instagram-embed-script",
    src: "https://www.instagram.com/embed.js",
    processEmbeds: () => {
      if (typeof window !== "undefined" && (window as any).instgrm) {
        (window as any).instgrm.Embeds.process();
      }
    },
  },
  x: {
    id: "twitter-embed-script",
    src: "https://platform.twitter.com/widgets.js",
    processEmbeds: () => {
      if (typeof window !== "undefined" && (window as any).twttr) {
        (window as any).twttr.widgets.load();
      }
    },
  },
  tiktok: {
    id: "tiktok-embed-script",
    src: "https://www.tiktok.com/embed.js",
  },
};

/**
 * Ensures the embed SDK script for a platform is loaded, then processes embeds.
 * No-ops for platforms without an SDK (e.g. YouTube, Facebook, Spotify).
 */
function ensureEmbedScriptLoaded(platform: SocialPlatform): void {
  const config = PLATFORM_SCRIPTS[platform];
  if (!config) return;

  const existingScript = document.getElementById(config.id);
  if (existingScript) {
    config.processEmbeds?.();
    return;
  }

  logger.consent(`Loading ${platform} script (embed consent given via overlay)`);
  const script = document.createElement("script");
  script.id = config.id;
  script.src = config.src;
  script.async = true;

  script.onload = () => {
    logger.consent(`${platform} script loaded successfully`);
    setTimeout(() => {
      config.processEmbeds?.();
    }, EMBED_REPROCESS_DELAY_MS);
  };

  script.onerror = () => {
    logger.error(`Failed to load ${platform} script`);
  };

  document.body.appendChild(script);
}

/**
 * Decodes base64 embed content and returns the HTML string
 */
function decodeEmbedContent(encoded: string): string {
  try {
    return decodeURIComponent(atob(encoded));
  } catch {
    logger.error("[EmbedConsent] Failed to decode embed content");
    return "";
  }
}

/**
 * Loads embed content for a specific wrapper element
 */
function loadEmbedContent(wrapper: HTMLElement): void {
  const encodedContent = wrapper.dataset.embedContent;
  const platform = wrapper.dataset.platform as SocialPlatform;

  if (!encodedContent) {
    logger.error("[EmbedConsent] No embed content found");
    return;
  }

  const contentContainer = wrapper.querySelector(SELECTORS.content);
  if (!contentContainer) {
    logger.error("[EmbedConsent] Content container not found");
    return;
  }

  // Decode and sanitize via DOMPurify before inserting (defense against compromised CMS content)
  const decodedContent = decodeEmbedContent(encodedContent);
  const sanitizedContent = sanitizeHtml(decodedContent);
  contentContainer.innerHTML = sanitizedContent;

  // Add consent-given class to show content and hide overlay
  wrapper.classList.add(SELECTORS.consentGiven);

  logger.consent(`Loaded ${platform} embed content`);

  // Ensure the platform's embed script is loaded, then process new DOM nodes
  setTimeout(() => {
    ensureEmbedScriptLoaded(platform);
  }, EMBED_REPROCESS_DELAY_MS);
}

/**
 * Handles consent button clicks
 */
function handleConsentClick(event: Event): void {
  const target = event.target as HTMLElement;
  const button = target.closest(SELECTORS.consentAction) as HTMLElement | null;

  if (!button) return;

  const action = button.dataset.consentAction;
  const wrapper = button.closest(SELECTORS.wrapper) as HTMLElement | null;

  if (!wrapper) return;

  const platform = wrapper.dataset.platform as SocialPlatform;

  // Both actions accept the vendor in Usercentrics;
  // "accept" loads just this embed, "accept-all" loads all of this platform on the page.
  if (action === "accept") {
    loadEmbedContent(wrapper);
  } else if (action === "accept-all") {
    const allWrappers = document.querySelectorAll<HTMLElement>(
      `${SELECTORS.wrapper}[data-platform="${platform}"]`
    );
    allWrappers.forEach((w) => {
      if (!w.classList.contains(SELECTORS.consentGiven)) {
        loadEmbedContent(w);
      }
    });
  }

  // Persist consent in Usercentrics (async, non-blocking for immediate UX)
  giveEmbedConsent(platform);
}

/**
 * Checks existing Usercentrics consent and auto-loads embeds where consent was already given.
 */
function checkExistingConsent(): void {
  const wrappers = document.querySelectorAll<HTMLElement>(SELECTORS.wrapper);

  wrappers.forEach((wrapper) => {
    const platform = wrapper.dataset.platform as SocialPlatform;

    if (hasEmbedConsent(platform) && !wrapper.classList.contains(SELECTORS.consentGiven)) {
      loadEmbedContent(wrapper);
    }
  });
}

let consentHandlerInitialized = false;

/**
 * Initializes the consent handler (idempotent).
 * - Attaches click listeners for consent buttons
 * - Checks for existing consent and auto-loads embeds
 */
export function initializeEmbedConsent(): void {
  if (isServer) return;
  if (consentHandlerInitialized) return;
  consentHandlerInitialized = true;

  // Use event delegation for consent buttons
  document.addEventListener("click", handleConsentClick);

  // Check existing consent after a small delay to ensure DOM is ready
  setTimeout(() => {
    checkExistingConsent();
  }, DOM_READY_DELAY_MS);
}

/**
 * Re-checks consent for newly added embeds (useful for SPA navigation)
 */
export function refreshEmbedConsent(): void {
  if (isServer) return;
  checkExistingConsent();
}

/**
 * Loads all embeds for a platform: decodes and inserts content, hides the overlay.
 * Called when Usercentrics consent is given (e.g. via banner accept).
 */
export function loadEmbedConsentDOM(platform: SocialPlatform): void {
  if (isServer) return;

  const wrappers = document.querySelectorAll<HTMLElement>(
    `${SELECTORS.wrapper}[data-platform="${platform}"]`
  );

  wrappers.forEach((wrapper) => {
    if (!wrapper.classList.contains(SELECTORS.consentGiven)) {
      loadEmbedContent(wrapper);
    }
  });
}

/**
 * Reverts embed DOM state for a platform: removes the loaded content,
 * re-shows the consent overlay. Called when Usercentrics consent is revoked.
 *
 * Also triggers the refresh banner since embed SDK scripts remain in browser
 * memory after removal — a full page reload is needed to fully unload them.
 */
export function revokeEmbedConsentDOM(platform: SocialPlatform): void {
  if (isServer) return;

  const wrappers = document.querySelectorAll<HTMLElement>(
    `${SELECTORS.wrapper}[data-platform="${platform}"]`
  );

  let revoked = false;

  wrappers.forEach((wrapper) => {
    if (!wrapper.classList.contains(SELECTORS.consentGiven)) return;

    // Remove consent-given class → CSS hides content, shows overlay
    wrapper.classList.remove(SELECTORS.consentGiven);

    // Clear the injected embed content (safe: setting to empty string)
    const contentContainer = wrapper.querySelector(SELECTORS.content);
    if (contentContainer) {
      contentContainer.textContent = "";
    }

    revoked = true;
  });

  // SDK scripts remain in memory after revocation — notify user a refresh is needed
  if (revoked && PLATFORM_SCRIPTS[platform]) {
    consentActions.markNeedsRefresh();
  }
}
